import React, { useState, useEffect } from 'react';
import { 
  BarChart3, Users, ShoppingBag, MessageCircle, FileText, 
  Euro, TrendingUp, AlertCircle, LogOut, Plus, Edit, Trash2
} from 'lucide-react';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { Link } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [dashResponse, articlesResponse, membersResponse, donationsResponse, messagesResponse] = await Promise.all([
        axios.get(`${API}/admin/dashboard`),
        axios.get(`${API}/articles`),
        axios.get(`${API}/members`),
        axios.get(`${API}/donations`),
        axios.get(`${API}/admin/contact/unread`)
      ]);

      setDashboardData({
        stats: dashResponse.data.stats,
        recentData: {
          articles: articlesResponse.data.slice(0, 5),
          members: membersResponse.data.slice(0, 5),
          donations: donationsResponse.data.slice(0, 5),
          unreadMessages: messagesResponse.data
        }
      });
    } catch (error) {
      console.error('Erreur lors du chargement du dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, icon: Icon, change, color = "primary" }) => (
    <Card className="border-border/50 hover:border-primary/20 transition-colors">
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-muted-foreground">{title}</p>
            <p className="text-3xl font-bold">{value}</p>
            {change && (
              <p className="text-xs text-muted-foreground mt-1">
                <TrendingUp className="inline h-3 w-3 mr-1" />
                {change}
              </p>
            )}
          </div>
          <div className={`w-12 h-12 bg-${color}/10 rounded-lg flex items-center justify-center`}>
            <Icon className={`h-6 w-6 text-${color}`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Chargement du dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-card border-b border-border/50 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <h1 className="text-2xl font-bold">
                <div className="padaid-logo inline">
                  <span className="pa-red">PA</span>
                  <span className="daid-green">DAID</span>
                </div> Admin
              </h1>
              <Badge variant="outline" className="text-xs">
                v1.0
              </Badge>
            </div>

            <div className="flex items-center space-x-4">
              <div className="text-right">
                <p className="text-sm font-medium">Bienvenue, {user?.username}</p>
                <p className="text-xs text-muted-foreground">Administrateur</p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={logout}
                className="text-muted-foreground hover:text-foreground"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Déconnexion
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation Tabs */}
      <div className="bg-card border-b border-border/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-8">
            {[
              { id: 'overview', label: 'Vue d\'ensemble', icon: BarChart3 },
              { id: 'articles', label: 'Articles', icon: FileText },
              { id: 'members', label: 'Membres', icon: Users },
              { id: 'products', label: 'Boutique', icon: ShoppingBag },
              { id: 'messages', label: 'Messages', icon: MessageCircle }
            ].map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === tab.id
                      ? 'border-primary text-primary'
                      : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  <span>{tab.label}</span>
                  {tab.id === 'messages' && dashboardData?.recentData.unreadMessages?.length > 0 && (
                    <Badge className="bg-red-500 text-white text-xs">
                      {dashboardData.recentData.unreadMessages.length}
                    </Badge>
                  )}
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'overview' && (
          <div className="space-y-8">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <StatCard
                title="Total Membres"
                value={dashboardData?.stats.total_members || 0}
                icon={Users}
                change="+12% ce mois"
                color="primary"
              />
              <StatCard
                title="Dons Collectés"
                value={`${dashboardData?.stats.total_donation_amount || 0}€`}
                icon={Euro}
                change="+8% ce mois"
                color="secondary"
              />
              <StatCard
                title="Articles Publiés"
                value={dashboardData?.stats.total_articles || 0}
                icon={FileText}
                change="+3 cette semaine"
                color="accent"
              />
              <StatCard
                title="Messages Non Lus"
                value={dashboardData?.recentData.unreadMessages?.length || 0}
                icon={MessageCircle}
                change="Nécessite attention"
                color="destructive"
              />
            </div>

            {/* Recent Activity */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="border-border/50">
                <CardHeader className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold">Articles Récents</h3>
                  <Button asChild variant="outline" size="sm"><Link to="/admin/audit/users">Journaux connexions/inscriptions</Link></Button>
                </CardHeader>
                <CardContent className="space-y-4">
                  {dashboardData?.recentData.articles?.map((article) => (
                    <div key={article.id} className="flex items-center justify-between p-3 bg-muted/20 rounded-lg">
                      <div className="flex-1">
                        <h4 className="font-medium text-sm">{article.title}</h4>
                        <p className="text-xs text-muted-foreground">Par {article.author}</p>
                      </div>
                      <Badge variant={article.status === 'published' ? 'default' : 'secondary'}>
                        {article.status}
                      </Badge>
                    </div>
                  )) || <p className="text-muted-foreground">Aucun article récent</p>}
                </CardContent>
              </Card>

              <Card className="border-border/50">
                <CardHeader>
                  <h3 className="text-lg font-semibold">Nouveaux Membres</h3>
                </CardHeader>
                <CardContent className="space-y-4">
                  {dashboardData?.recentData.members?.map((member) => (
                    <div key={member.id} className="flex items-center justify-between p-3 bg-muted/20 rounded-lg">
                      <div className="flex-1">
                        <h4 className="font-medium text-sm">{member.name}</h4>
                        <p className="text-xs text-muted-foreground">{member.email}</p>
                      </div>
                      <Badge variant="outline">
                        {member.membership_type}
                      </Badge>
                    </div>
                  )) || <p className="text-muted-foreground">Aucun nouveau membre</p>}
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {activeTab === 'messages' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold">Messages Non Lus</h2>
              <Badge variant="destructive">
                {dashboardData?.recentData.unreadMessages?.length || 0} nouveaux
              </Badge>
            </div>

            <div className="space-y-4">
              {dashboardData?.recentData.unreadMessages?.map((message) => (
                <Card key={message.id} className="border-border/50">
                  <CardContent className="p-6">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="font-semibold">{message.subject}</h3>
                        <p className="text-sm text-muted-foreground">
                          De: {message.name} ({message.email})
                        </p>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Badge variant="outline">{message.message_type}</Badge>
                        <Button size="sm" variant="outline">
                          Marquer comme lu
                        </Button>
                      </div>
                    </div>
                    <p className="text-sm">{message.message}</p>
                    <p className="text-xs text-muted-foreground mt-2">
                      Reçu le {new Date(message.created_at).toLocaleDateString('fr-FR')}
                    </p>
                  </CardContent>
                </Card>
              )) || (
                <div className="text-center py-12">
                  <MessageCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">Aucun message non lu</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Autres tabs à implémenter */}
        {activeTab !== 'overview' && activeTab !== 'messages' && (
          <div className="text-center py-12">
            <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">Section en développement</h3>
            <p className="text-muted-foreground">
              La gestion {activeTab === 'articles' ? 'des articles' : 
                         activeTab === 'members' ? 'des membres' : 
                         'de la boutique'} sera disponible prochainement.
            </p>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminDashboard;